// Draft-editing model for the conditional rules dialog (`../rules.vue`).
// Pure helpers only (no Vue reactivity) so they're easy to reason about from
// each child component; the dialog owns the reactive draft array itself.
import {
  CONDITIONAL_RULE_ACTION_DEFINITIONS,
  DEFAULT_CONDITIONAL_RULE_ACTIONS,
  normalizeConditionalRules,
  getAllowedConditionOperators,
  type ConditionalRuleAction,
  type ConditionOperator,
  type ConditionSource,
  type ConditionStateField,
  type ConditionValueType,
  type ConditionalRule,
} from "../../../lib/conditionalRules";

/** Source shape while a rule is mid-edit — fields fill in as the author picks a type. */
export type DraftConditionSourceType = ConditionSource["type"] | "";

export interface DraftConditionSource {
  type: DraftConditionSourceType;
  blockId?: string;
  dataPath?: string[];
  key?: string;
  valueType?: "string" | "file" | "number" | "boolean";
  field?: string;
}

export interface DraftRule {
  id: string;
  source: DraftConditionSource;
  condition: string;
  value: unknown;
  action: string;
}

/** Flattened block summary used for the "Component state" block picker. */
export interface BlockSummary {
  id: string;
  componentId?: string;
  label: string;
  type?: string;
  icon?: string;
}

export function getRuleActionOptions(
  actions: readonly ConditionalRuleAction[],
): ReadonlyArray<{ value: string; label: string }> {
  return CONDITIONAL_RULE_ACTION_DEFINITIONS
    .filter((definition) => actions.includes(definition.value))
    .map((definition) => ({
      value: definition.label,
      label: definition.label,
    }));
}

export function getAllowedRuleActions(
  additionalActions: readonly ConditionalRuleAction[] = [],
): ConditionalRuleAction[] {
  return [...new Set([
    ...DEFAULT_CONDITIONAL_RULE_ACTIONS,
    ...additionalActions,
  ])];
}

export function createDraftRule(): DraftRule {
  return {
    id: crypto.randomUUID(),
    source: { type: "" },
    condition: "",
    value: "",
    action: "",
  };
}

function toDraftSource(source?: ConditionSource): DraftConditionSource {
  if (!source) return { type: "" };
  if (source.type === "answer") {
    return { type: "answer", blockId: source.blockId };
  }
  if (source.type === "user-state") {
    return {
      type: "user-state",
      dataPath: source.dataPath,
      key: source.key,
      valueType: source.valueType,
    };
  }
  if (source.type === "user-metric") {
    return {
      type: "user-metric",
      dataPath: source.dataPath,
      key: source.key,
    };
  }
  return { type: "component-state", blockId: source.blockId, field: source.field };
}

/** Load persisted (possibly legacy) conditions into an editable draft array. */
export function loadDraftRules(rawConditions: unknown): DraftRule[] {
  return normalizeConditionalRules(rawConditions).map((rule) => ({
    id: rule.id || crypto.randomUUID(),
    source: toDraftSource(rule.source),
    condition: rule.condition ?? "",
    value: rule.value ?? "",
    action: rule.action ?? "",
  }));
}

/** Persist the draft array as clean, normalized conditional rules. */
export function serializeDraftRules(rules: DraftRule[]): ConditionalRule[] {
  return normalizeConditionalRules(
    rules.map((rule) => ({
      id: rule.id,
      source: rule.source.type ? rule.source : undefined,
      condition: rule.condition,
      value: rule.value,
      action: rule.action,
    })),
  );
}

/** Recursively flatten the content tree into a friendly-labelled block list. */
export function flattenBlocks(blocks: unknown): BlockSummary[] {
  const result: BlockSummary[] = [];

  const visit = (nodes: unknown) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node && typeof node === "object") {
        const block = node as Record<string, unknown>;
        if (typeof block.id === "string") {
          const label = block.title || block.name || block.label || block.placeholder || block.id;
          result.push({
            id: block.id,
            componentId: typeof block.componentId === "string" ? block.componentId : undefined,
            label: String(label),
          });
        }
        visit(block.children);
      }
    }
  };

  visit(blocks);
  return result;
}

export interface RuleFieldMeta {
  valueType: ConditionValueType;
  options?: Array<{ label: string; value: string | number | boolean }>;
  unit?: string;
  operators: readonly ConditionOperator[];
}

const DEFAULT_FIELD_META: RuleFieldMeta = {
  valueType: "string",
  operators: getAllowedConditionOperators("string"),
};

export interface AnswerableBlockSummary extends BlockSummary {
  answerType: "single-choice" | "multi-choice" | "text" | "number";
  options?: Array<{ id: string; name?: string }>;
}

export function resolveFieldMeta(
  source: DraftConditionSource,
  ctx: {
    answerableBlocks: AnswerableBlockSummary[];
    allBlocksById: Map<string, BlockSummary>;
    getComponentStateFields: (componentId?: string) => ConditionStateField[];
  },
): RuleFieldMeta {
  if (source.type === "answer") {
    const block = ctx.answerableBlocks.find((candidate) => candidate.id === source.blockId);
    if (!block) return DEFAULT_FIELD_META;
    if (block.answerType === "number") {
      return { valueType: "number", operators: getAllowedConditionOperators("number") };
    }
    if (block.answerType === "text") {
      return { valueType: "string", operators: getAllowedConditionOperators("string") };
    }
    const options = (block.options ?? []).map((option) => ({
      label: option.name || option.id,
      value: option.id,
    }));
    return { valueType: "enum", options, operators: getAllowedConditionOperators("enum") };
  }

  if (source.type === "user-state") {
    const valueType = source.valueType ?? "string";
    return { valueType, operators: getAllowedConditionOperators(valueType) };
  }

  if (source.type === "user-metric") {
    return { valueType: "number", operators: getAllowedConditionOperators("number") };
  }

  if (source.type === "component-state") {
    const block = source.blockId ? ctx.allBlocksById.get(source.blockId) : undefined;
    const fields = ctx.getComponentStateFields(block?.componentId);
    const field = fields.find((candidate) => candidate.key === source.field);
    if (!field) return DEFAULT_FIELD_META;
    return {
      valueType: field.valueType,
      options: field.options,
      unit: field.unit,
      operators: getAllowedConditionOperators(field.valueType),
    };
  }

  return DEFAULT_FIELD_META;
}

function sourceIsComplete(source: DraftConditionSource): boolean {
  if (source.type === "answer") return Boolean(source.blockId);
  if (source.type === "user-state") return Boolean(source.key?.trim());
  if (source.type === "user-metric") return Boolean(source.key?.trim());
  if (source.type === "component-state") return Boolean(source.blockId && source.field);
  return false;
}

/** Whether a draft row has everything required to be saved. */
export function isDraftRuleComplete(
  rule: DraftRule,
  meta: RuleFieldMeta,
  options: { requireAction?: boolean } = {},
): boolean {
  if (!sourceIsComplete(rule.source)) return false;
  if (!rule.condition || !meta.operators.includes(rule.condition as ConditionOperator)) return false;
  if (options.requireAction !== false && !rule.action) return false;
  if (rule.value === undefined || rule.value === null || rule.value === "") return false;
  return true;
}
